import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { NAV_GROUPS, NAV_ITEMS, type NavItem } from "./nav-items";
import { usePermissions } from "@/hooks/use-permissions";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SidebarNavProps {
  /** Icon-rail mode: labels hidden, tooltips supply the name. */
  collapsed?: boolean;
  /** Called after navigating — closes the mobile drawer. */
  onNavigate?: () => void;
}

export function SidebarNav({ collapsed = false, onNavigate }: SidebarNavProps) {
  const { can } = usePermissions();

  /*
    Items the admin may not open are filtered out rather than disabled.

    A greyed-out link still tells a Team Leader there is a screen they cannot
    have, and invites the conversation; absent is quieter and truer to how the
    rest of this sidebar works, where an item appears when the screen exists
    for you.

    While permissions load, `can` says no, so gated items appear a moment after
    the Dashboard rather than appearing and then vanishing. A section whose
    items are all hidden is hidden with them — an empty heading is a dead end.
  */
  const sections = useMemo(() => {
    const visible = NAV_ITEMS.filter(
      (item) => item.permission === undefined || can(item.permission),
    );
    return [
      { group: null, items: visible.filter((item) => item.group === undefined) },
      ...NAV_GROUPS.map((group) => ({
        group,
        items: visible.filter((item) => item.group === group),
      })),
    ].filter((section) => section.items.length > 0);
  }, [can]);

  return (
    <nav aria-label="Main" className="flex flex-col gap-4">
      {sections.map(({ group, items }) => (
        <div key={group ?? "top"} className="flex flex-col gap-0.5">
          {group !== null ? (
            collapsed ? (
              // The rail has no room for a heading; a rule keeps the sections
              // apart without one.
              <div className="mx-2 mb-1 border-t border-border" aria-hidden="true" />
            ) : (
              <h2 className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                {group}
              </h2>
            )
          ) : null}
          {items.map((item) => (
            <SidebarLink
              key={item.to}
              item={item}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
    </nav>
  );
}

function SidebarLink({
  item: { label, to, icon: Icon, end = false },
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: (() => void) | undefined;
}) {
  const link = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex h-8 items-center gap-2.5 rounded-md px-2 text-sm transition-colors touch:h-11",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed && "justify-center px-0",
          // Active is carried by the fill and the weight as well as the icon's
          // colour, so it never rests on colour alone.
          isActive
            ? "bg-sidebar-active font-medium text-sidebar-foreground"
            : "text-muted-foreground hover:bg-sidebar-active/60 hover:text-sidebar-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn("size-4 shrink-0", isActive && "text-primary")}
            aria-hidden="true"
          />
          {collapsed ? (
            <span className="sr-only">{label}</span>
          ) : (
            <span className="truncate">{label}</span>
          )}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
